<template lang="pug">
  .make-an-entry-photo
    .make-an-entry-content
      .entry-visibility(v-if="isJournal")
        BField(
          label="Image Visible To"
          custom-class="has-text-grey is-small"
        )
          BSelect.visibility-select(
            placeholder="Select"
            v-model="visibility"
            size="is-small"
          )
            option.visibility-option(
              v-for="(option,index) in ['Only Me', 'Friends', 'Anyone']"
              :value="index"
              :key="option"
            ) {{ option }}
      section.section.pb-1.pt-4
        WtTitle(h4 bold) Upload Image
          BTooltip(
            label="Valid File Types: jpeg, gif, and png."
            position="is-top"
            animated
          )
            BIcon(icon="information" size="is-small" type="is-grey")

        .columns.is-mobile
          .column.level
            .level-item(v-if="showUpload")
              BField
                BUpload(
                  multiple
                  drag-drop
                  native
                  @input="previewImage"
                  accept="image/jpeg,image/gif,image/png"
                )
                  .uploader-content.level(ref="uploader")
                    .level-left
                      .level-item
                        BIcon(icon="upload" size="is-medium")
                    .level-right
                      .level-item
                        .has-text-grey.is-size-7 Drop your files here or click to upload.
            BField.level(v-else)
              .level-item
                figure.image.is-128x128
                  img(:src="previewImageData || entryItem.image" alt="Your photo.")
          .column.is-3-desktop.is-4-mobile.pt-6(v-show="!_.isEmpty(newPhoto) || !_.isEmpty(entryItem.image)")
            BField
              WtButton(
                style="width: 80px"
                color="green"
                outlined
                size="small"
                :disabled="deletingInProgress"
                @click.native="replaceImage"
              ) REPLACE
            BField
              WtButton(
                style="width: 80px"
                color="red"
                outlined
                size="small"
                :loading="deletingInProgress"
                @click.native="deletePhoto"
              ) {{ deletingInProgress ? "DELETING" : "DELETE" }}

    .level.is-mobile.program-settings-form-controls
      .level-left
      .level-right
        .level-item
          WtButton(
            is-text
            color="white"
            style="text-decoration:none"
            size="medium"
            @click.native="reset"
            :disabled="disableReset"
          ) Reset
        .level-item
          WtButton(
            @click.native="saveEntry"
            :loading="uploadInProgress"
            :disabled="disableSave || uploadInProgress"
          ) SAVE
</template>

<script>
import imageCompression from "browser-image-compression";
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import MakeAnEntryFooter from "@@/Members/MakeAnEntry/MakeAnEntryFooter";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";

export default {
  name: "MakeAnEntryPhoto",

  components: { WtTitle, WtButton, MakeAnEntryFooter },

  mixins: [dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    fetchAction: {
      type: Function,
      default: () => {},
      required: true
    },

    // "journal" or "discussion"
    type: {
      type: String,
      default: "journal"
    },

    entryItem: {
      type: Object,
      default: () => {},
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    visibilityUpdated: {
      type: Number,
      default: 2
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      newPhoto: [],
      previewImageData: "",
      showUpload: true,
      deletingInProgress: false,
      uploadInProgress: false,
      visibility: 2,
      imageCompressionError: false,
      dataURL: null
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"]),

    isJournal() {
      return this.type === "journal";
    },

    appendVisibility() {
      if (!this._.isEmpty(this.entryItem)) {
        // either it's changed or it might have been changed and changed back to what it was
        return (
          this.visibility !== this.entryItem.visibility ||
          this.formData.has("visibility")
        );
      }
      return true;
    }
  },

  watch: {
    newPhoto(newPhoto) {
      if (!this._.isEmpty(newPhoto)) {
        this.previewImage(newPhoto);
        this.formData.set("image", newPhoto);
        this.$emit("formDataUpdated", true);
      } else {
        this.formData.delete("image");
      }
    },

    visibility() {
      if (this.appendVisibility) {
        this.formData.set("visibility", this.visibility);
        this.formData.set("textVisibility", this.visibility);
        this.$emit("formDataUpdated", true);
        this.$emit("visibilityUpdated", this.visibility);
      }
    },

    visibilityUpdated(newValue) {
      this.visibility = newValue;
    },

    previewImageData(newImage) {
      // have to wait for BUpload to process previewImage *and then* hide the BUpload :(
      if (!this._.isEmpty(newImage)) {
        this.showUpload = false;
      }
    }
  },

  mounted() {
    if (!this._.isEmpty(this.entryItem)) {
      this.showUpload = this._.isEmpty(this.entryItem.image);
      this.visibility = this.entryItem.visibility;
    }
  },

  methods: {
    ...mapActions("members", [
      "updateJournal",
      "storeJournal",
      "deleteJournalPhoto"
    ]),
    ...mapActions("challenges", [
      "updateDiscussion",
      "storeDiscussion",
      "deleteDiscussionPhoto"
    ]),
    ...mapActions(["snackBar"]),

    async saveEntry() {
      this.uploadInProgress = true;

      if (this.newPhoto) {
        this.type === "discussion"
          ? await this.saveEntryWrapper(
              this.storeDiscussion,
              this.updateDiscussion
            )
          : await this.saveEntryWrapper(this.storeJournal, this.updateJournal);
      }

      this.uploadInProgress = false;
    },

    async reset() {
      const item = "image";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      await this.deletePhoto();
      this.showUpload = true;
      this.visibility = this.entryItem.visibility;
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    },

    async deletePhoto() {
      if (this._.isEmpty(this.entryItem.image)) {
        this.clearImagePreview();
      } else {
        this.type === "discussion"
          ? await this.deletePhotoWrapper(this.deleteDiscussionPhoto)
          : await this.deletePhotoWrapper(this.deleteJournalPhoto);
      }
    },

    async previewImage(file) {
      file = file[0] || false;
      const options = {
        maxSizeMB: 10, // apiv3 maxsize
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      let blob = null;
      try {
        blob = await imageCompression(file, options);

        this.dataURL = await imageCompression.getDataUrlFromFile(blob);

        this.newPhoto = blob;

        this.showReplace = true;
        this.previewImageData = this.dataURL;
      } catch (error) {
        this.imageCompressionError = true;
      }
    },

    async deletePhotoWrapper(action) {
      this.deletingInProgress = true;

      const payload = {
        itemId: this.entryItem.id
      };

      await action(payload).then(() => {
        this.snackBar({
          message: "Your photo has been deleted."
        });
      });

      this.fetchAction();

      this.clearImagePreview();
    },

    clearImagePreview() {
      this.deletingInProgress = false;
      this.newPhoto = [];
      this.showUpload = true;
      this.previewImageData = null;
    },

    async replaceImage() {
      // await this.deletePhoto();
      this.showUpload = true;
      await this.$forceUpdate();
      this.$refs.uploader.click();
      // and note, the 'previewImageData' watcher waits for BUpload and then hides BUpload
    }
  }
};
</script>

<style lang="sass">
.uploader-content
  padding: 3rem
  .level-right
    margin-left: 1rem
    .level-item
      width: 7rem
      overflow-wrap: wrap
.entry-visibility
  position: absolute
  right: 1rem
  top: .5rem

.program-settings-form-controls
  margin-top: 20px
</style>
